/**
 * IP-based country detection used as a fallback when navigator.language
 * isn't specific enough (e.g. generic en-US on a phone in France).
 *
 * Runs only on the very first app launch (controlled via localStorage flag),
 * so subsequent loads have zero latency cost and the user's manual choice in
 * Profile → Settings is never overridden.
 */

const STORAGE_KEY = 'fricamp_country_lang_init';
const TIMEOUT_MS = 3000;

const COUNTRY_TO_LANGUAGE: Record<string, string> = {
  // Norwegian
  NO: 'no',
  SJ: 'no', // Svalbard

  // English (default for English-speaking countries)
  US: 'en', GB: 'en', CA: 'en', AU: 'en', NZ: 'en', IE: 'en', ZA: 'en',
  IN: 'en', SG: 'en', HK: 'en', PH: 'en',

  // German-speaking
  DE: 'de', AT: 'de', CH: 'de', LI: 'de',

  // Polish
  PL: 'pl',

  // Spanish-speaking
  ES: 'es', MX: 'es', AR: 'es', CO: 'es', PE: 'es', VE: 'es', CL: 'es',
  EC: 'es', GT: 'es', CU: 'es', DO: 'es', BO: 'es', UY: 'es', PY: 'es',
  CR: 'es', PA: 'es', SV: 'es', HN: 'es', NI: 'es', PR: 'es',

  // French-speaking
  FR: 'fr', LU: 'fr', MC: 'fr', SN: 'fr', CI: 'fr', CM: 'fr',

  // Dutch-speaking (~60% of Belgium is Flemish; Wallonia users can switch manually)
  NL: 'nl', BE: 'nl',

  // Swedish
  SE: 'sv',

  // Nordic neighbors (route to Norwegian until those languages are added)
  DK: 'no',
  FI: 'no', // Few Finnish speakers, route to NO until FI added
  IS: 'no',
};

const SUPPORTED = new Set(['en', 'no', 'de', 'pl', 'es', 'fr', 'nl', 'sv']);

async function fetchCountry(): Promise<string | null> {
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    // ipapi.co — free tier 30k/month, no auth, CORS-enabled
    const res = await fetch('https://ipapi.co/json/', { signal: controller.signal });
    if (!res.ok) return null;
    const data = await res.json();
    const code = (data?.country_code as string)?.toUpperCase?.() ?? null;
    return code && /^[A-Z]{2}$/.test(code) ? code : null;
  } catch {
    return null;
  } finally {
    clearTimeout(timeout);
  }
}

/**
 * Detect a language fallback by IP country.
 * Runs at most once per device. Returns the language to switch to, or null
 * if no override is needed (the current detected language is fine).
 */
export async function detectInitialLanguageByCountry(currentLang: string): Promise<string | null> {
  try {
    if (localStorage.getItem(STORAGE_KEY)) return null;
  } catch {
    // localStorage may be blocked (private mode); skip detection
    return null;
  }

  const country = await fetchCountry();
  try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
  if (!country) return null;

  const expected = COUNTRY_TO_LANGUAGE[country];
  if (!expected || !SUPPORTED.has(expected)) return null;

  // Only override if the current language disagrees AND current looks generic
  // (i.e. probably a default fallback rather than user's explicit choice).
  const cur = (currentLang || '').slice(0, 2).toLowerCase();
  if (cur === expected) return null;

  // Heuristic: override only if current is fallback ('no') or generic English.
  // Don't override a confident specific match — if device says 'de' but IP says
  // 'fr', trust the device.
  if (cur === 'no' || cur === 'en') return expected;
  return null;
}
